export interface SupplierType {
  id: number;
  name: string;
}

export type ProducerType = {
  id: number;
  name: string;
  company_type: string[];
  is_customer: boolean;
  country: string;
};

export type ProducerMillType = {
  id: number;
  name: string;
  company_type: string[];
  is_customer: boolean;
  country: string;
};

export type CargoPackagingProps = {
  id: number;
  customer: SupplierType;
  importer: SupplierType;
  supplier: SupplierType;
  producer: ProducerType;
  producer_mill: ProducerMillType;
  production_method: { id: number; name: string };
  commodity: string;
  commodity_it: string;
  ocean_vessel: string;
  id_number: string;
  thickness: number;
  width: number;
  length: number;
  pieces: number;
  net_weight: number;
  gross_weight: number;
  grade: string;
  heat: string;
  comment_1: string;
  comment_2: string;
  ddt_customer_reference_number: string;
  customer_reference_number: string;
  mbl_number: string;
  bl_container_id: number;
  bl_commodity_id: number;
  bl_container: {
    id: number;
    number: string;
    container_type: string;
    equipment_type: string;
    pieces: number;
    extra_gauge: string;
    seal: string;
    net_weight: number;
    gross_weight: number;
    volume: number;
  };
  shipping_case: {
    id: number;
    label: string;
  };
};

export type FcrProps = {
  fcr_bank: string;
  fcr_release_date: string;
  fcr_number: string;
  fcr_status: string;
};

export type UnloadingProps = {
  pile: string;
  rows: number;
  tiers: number;
  mark: string;
  deposit_reference: string;
  depot: string;
  claims: string;
  transport_reason: string;
  unloading_comment: string;
  consignee: { id: number; name: string };
  discharge_terminal: { id: number; name: string };
  discharge_terminal_id: number;
  warehouse: { id: number; name: string };
  warehouse_id: number;
  depositor: {
    id: number;
    name: string;
  };
  destination: {
    id: number;
    address: string;
    city: {
      id: number;
      name: string;
      province: string;
      country: string;
      post_code: string;
      title: string;
    };
    customer: {
      id: number;
      name: string;
    };
    delivery_address: string;
    delivery_city: {
      id: number;
      name: string;
      province: string;
      country: string;
      post_code: string;
      title: string;
      deleted_at: string;
    };
  };
};

export type DeliveryProps = {
  delivery_status: string;
  loading: {
    id: number;
    status: string;
    created_at: string;
    transport_reason: string;
    mark: string;
    ddt_number: number;
    ddt_created_at: string;
    delivery_info: {
      id: number;
      label: string;
      volume_weight: number;
      seq_number: number;
      delivery_truck: {
        id: number;
        delivery: {
          shipping_case_id: number;
          warehouse_shifting_id: number;
          reference_order: string;
          tc_customer: {
            id: number;
            name: string;
          };
          transport_contactor: string;
          voyage_number: number;
          id: number;
          truck_supplier: {
            id: number;
            name: string;
          };
        };
        truck_plate: string;
        seq_number: number;
        truck_kind: {
          id: number;
          service_id: number;
          name: string;
          name_it: string;
          macro_category: string;
        };
      };
    };
  };
};

export enum MACRO_CATEGORY {
  NORMAL = "Camion normale",
  CONTAINER = "Camion contenitore",
  TRAIN = "Treno",
  EXCEPTIONAL = "Camion eccezionale",
}

export type WeighingPops = {
  weighing_number: string;
  weighing_position: number;
  weighing_sector: number;
  weighing_customs_procedure: number;
  weighing_warehouse_code: number;
};

export type CustomProps = {
  supplier_invoice_number: string;
  invoice_amount: number;
  invoice_value: number;
  invoice_currency: string;
  hs_code: string;
  incoterm: string;
  caad_code: string;
  intro_customs_bill_draft: string;
  intro_customs_bill_status: string;
  intro_customs_bill_type: string;
  intro_mrn_code: string;
  customs_bill_draft: string;
  customs_release_code: string;
  customs_release_date: string;
  mrn_code: string;
  customs_bill_status: string;
  customs_bill_type: string;
};

export type CargoPackagingResponse = {
  items:
    | (CargoPackagingProps &
        FcrProps &
        UnloadingProps &
        WeighingPops &
        CustomProps &
        DeliveryProps & { offer_id: number; shipping_case_id: number })[]
    | null;
  meta: Record<string, number> | null;
  total: number;
  page: number;
  size: number;
};

export type CustomPackagingProps = {
  invoice: string;
  incoterms: string;
  invoice_value_currency_per_ton: number;
  origin_charge: string;
  freight: string;
  insurance: string;
  cif_price_currency_eur: number;
  cif_eur: number;
  hs_code: string;
  caad_additional_code: string;
  customs_bill_draft: string;
  customs_status: string;
  customs_document_type: string;
  customs_document_number_mrn: string;
  a14: string;
  a14_date: string;
};

export type UnloadingPackagingProps = {
  terminal_of_discharge: string;
  deposit_reference: string;
  warehousing: string;
  pile: string;
  row: number;
  tiers: number;
  note: string;
  claims: string;
  marks: string[];
  sender_depositor: string;
  consignee: string;
  destination: string;
  loading_order: string;
  date: string;
  reason_for_transport: string[];
};

export enum CARGO_PACKAGING_FIELDS {
  SC_NUMBER = "Shipping Case",
  CUSTOMER = "Customer",
  IMPORTER = "Importer",
  SUPPLIER_OF_GOODS = "Supplier of the Goods",
  MILL_INSTALLATION = "Mill Installation",
  MILL_OPERATOR = "Mill Operator",
  PRODUCTION_METHOD = "Production Method",
  BILL_OF_LADING = "Bill of Lading",
  VESSEL = "Vessel",
  COMMODITIES = "Commodities",
  NUMBER = "ID Number",
  THICKNESS = "Thickness",
  WIDTH = "Width",
  LENGTH = "Length",
  PIECES = "Pieces",
  NET_WEIGHT = "Net Weight",
  GROSS_WEIGHT = "Gross Weight",
  GRADE = "Grade",
  HEAT = "Heat",
  NOTE1 = "Note 1 (DDT)",
  NOTE2 = "Note 2 (DDT)",
  DDT_CUSTOMS_REFERENCE = "DDT Customer Reference",
  CUSTOMER_REFERENCE = "Customer Reference",
  CONTAINER = "Container",
}

export enum CUSTOM_PACKAGING_FIELDS {
  INVOICE = "Invoice",
  INCOTERM = "Incoterms",
  INVOICE_VALUE_CURRENCY_PER_TON = "Invoice value currency / ton",
  INVOICE_AMOUNT = "Invoice amount",
  INVOICE_VALUE_CURRENCY = "Invoice value currency",
  HS_CODE = "HS code",
  CAAD_ADDITIONAL_CODE = "Cadd additional code",
  CUSTOMS_BILL_DRAFT_INTRO = "Customs Bill Draft Intro",
  CUSTOMS_DOCUMENT_TYPE_INTRO = "Customs Document Type Intro",
  CUSTOMS_DOCUMENT_MRN_INTRO = "Customs Document MRN Intro",
  CUSTOMS_BILL_DRAFT = "Customs bill draft",
  CUSTOMS_STATUS = "Customs Status",
  CUSTOMS_DOCUMENT_TYPE = "Customs document type",
  CUSTOMS_DOCUMENT_NUMBER_MRN = "Customs document MRN",
  A14 = "A14",
  A14_DATE = "A14 date",
}

export enum UNLOADING_PACKAGING_FIELDS {
  TERMINAL_OF_DISCHARGE = "Terminal of Discharge",
  DEPOSIT_REFERENCE = "Deposit Reference",
  WAREHOUSING = "Warehousing",
  PILE = "Pile",
  ROW = "Row",
  TIERS = "Tiers",
  DEPOT = "Depot",
  NOTE = "Note",
  CLAIMS = "Claims",
  MARKS = "Marks",
  SENDER_DEPOSITOR = "Sender/Depositor",
  CONSIGNEE = "Consignee",
  DESTINATION = "Destination",
  LOADING_ORDER = "Loading Order",
  DATE_UNLOADING = "DDT date",
  REASON_FOR_TRANSPORT = "Reason for Transport",
}

export enum FCR_PACKAGING_FIELDS {
  FCR = "FCR",
  BANK = "Bank",
  STATUS_FCR = "Status",
  DATE_OF_RELEASE = "Date of Release",
}

export enum DELIVERY_PACKAGING_FIELDS {
  TRANSPORT_CONTRACTOR = "Transport Contractor",
  STATUS_DELIVERY = "Status",
  TRANSPORT_DOCUMENT = "DDT",
  CARRIER = "Carrier",
  MEANS_OF_TRANSPORT = "Means of Transport",
}

export enum WEIGHING_PACKAGING_FIELDS {
  WEIGHING_NUMBER = "Weighing number",
  WEIGHING_POSITION = "Number",
  CUSTOMS_PROCEDURE = "Procedure",
  WAREHOUSE_YARD = "Warehouse / Yard",
  SECTOR = "Sector",
}

export type PaginationModel = {
  page: number;
  pageSize: number;
};

export type RequestWmsParams = {
  id?: number;
  id__gte?: number;
  id__lte?: number;
  pieces__gte?: number;
  pieces__lte?: number;
  id_number?: string;
  thickness__gte?: number;
  thickness__lte?: number;
  width__gte?: number;
  width__lte?: number;
  length__gte?: number;
  length__lte?: number;
  net_weight__gte?: number;
  net_weight__lte?: number;
  gross_weight__gte?: number;
  gross_weight__lte?: number;
  comment_1?: string;
  comment_2?: string;
  order_by?: string;
  bl_commbl_commodity__customer_reference_number?: string;
  bill_of_lading__mbl_number?: string;
  customer__id__in?: string;
  ocean_vessel__name?: string;
  commodity__name?: string;
  importer__id__in?: string;
  producer__id__in?: string;
  supplier__id__in?: string;
  bl_container__number?: string;
  grade?: string;
  heat?: string;
  page: number;
  size: number;
};

export type ColumnOption = {
  id: string;
  label: string;
  section: string;
};

export type FilterElementType<T> = T extends { element: "Textfield" }
  ? string | null
  : T extends { element: "Autocomplete"; multi: true }
    ? any[] | null
    : T extends { element: "Autocomplete"; multi: false }
      ? any | null
      : T extends { element: "Datepicker" }
        ? [string | null, string | null] | null
        : never;

export type FiltersType = {
  label?: string;
  offer_id?: number;
} & {
  [key: string]:
    | string
    | string[]
    | number
    | boolean
    | null
    | undefined
    | { label: string; id: number };
};

export type FilterValue<K extends keyof FiltersType> = FiltersType[K];
